import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box, Flex } from '@rebass/grid';
import { gql, NetworkStatus } from '@apollo/client';
import range from 'lodash.range';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import searchParams from '../constants/searchParams';
import { SearchValues } from '../entities/SearchValues';
import { SearchRentalUnitsQuery, useSearchRentalUnitsQuery } from '../generated/graphql';
import useAnalyticsUnitViewList from '../hooks/analytics/useAnalyticsUnitViewList';
import useQueryParams from '../hooks/useQueryParams';
import { convertBookingToParamsInput, pickBookingFromParams, withDefaultBookingValues } from '../utils/trip';
import Cell from './designsystem/Cell';
import Formed, { Handler } from './Formed';
import SearchBox from './SearchBox';
import SearchHit from './SearchHit';
import { useTenantContext } from '../context/TenantContext';

export const query = gql`
    query SearchRentalUnits(
        $slug: String!
        $params: UnitSearchParamsInput
        $limit: Int
        $offset: Int
        $badgeHandle: String!
        $badge2Handle: String!
    ) {
        accommodation(slug: $slug) {
            id
            searchRentalUnits(params: $params, limit: $limit, offset: $offset) {
                ...SearchRentalUnits
            }
        }
    }

    fragment SearchRentalUnits on UnitSearchResult {
        totalHits
        hits {
            ...SearchHit
        }
    }
`;

interface Props {
    slug: string;
}

type Values = Pick<SearchValues, 'booking'>;

const SearchRentalUnits: FC<React.PropsWithChildren<Props>> = ({ slug }) => {
    const [params, setParams] = useQueryParams(searchParams);
    const { brandConfig } = useTenantContext();
    const { data, loading, fetchMore, networkStatus } = useSearchRentalUnitsQuery({
        variables: {
            slug,
            params: convertBookingToParamsInput(params),
            limit: 100,
            badgeHandle: brandConfig.badge?.handle ?? '',
            badge2Handle: brandConfig.badge2?.handle ?? '',
        },
        notifyOnNetworkStatusChange: true,
        ssr: false,
    });

    const totalHits = data?.accommodation?.searchRentalUnits.totalHits ?? 0;
    const hits = data?.accommodation?.searchRentalUnits.hits ?? [];

    const moreAvailable = totalHits > hits.length;

    const loadMoreHandler = async () => {
        if (loading) {
            return;
        }
        await fetchMore({
            updateQuery: (prev, { fetchMoreResult }): SearchRentalUnitsQuery => {
                if (!fetchMoreResult?.accommodation) {
                    return prev;
                }
                const mergedSearchRentalUnits = [
                    ...(prev.accommodation?.searchRentalUnits.hits ?? []),
                    ...(fetchMoreResult.accommodation?.searchRentalUnits.hits ?? []),
                ];

                return {
                    ...prev,
                    accommodation: {
                        ...prev.accommodation,
                        ...fetchMoreResult.accommodation,
                        searchRentalUnits: {
                            ...fetchMoreResult.accommodation?.searchRentalUnits,
                            hits: mergedSearchRentalUnits,
                        },
                    },
                };
            },
        });
    };

    const autoSaveHandler: Handler<Values> = useCallback(
        values => {
            // reflect the values in the query string
            setParams({
                ...values.booking,
            });
        },
        [setParams]
    );

    const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
    const noResults = data?.accommodation && hits.length === 0;
    const booking = pickBookingFromParams(params);
    useAnalyticsUnitViewList(hits);

    return (
        <Box>
            <Formed<Values>
                skipPrompt
                handleAutoSave={autoSaveHandler}
                initialValues={{
                    booking: withDefaultBookingValues(booking),
                }}
                enableReinitialize
            >
                {() => (
                    <SearchBox hideFlexibleOptions variant="accommodationSearch" active={['tripgroup', 'datepicker']} />
                )}
            </Formed>
            <Grid moreAvailable={moreAvailable && !noResults}>
                {loading && range(3).map(i => <SearchHit key={i} loading />)}
                {!loading &&
                    hits.map(hit => (
                        <SearchHit
                            key={hit.rentalUnit.id}
                            rentalUnit={hit.rentalUnit}
                            trips={hit.trips}
                            unitParams={booking}
                        />
                    ))}
                {noResults && !moreAvailable && (
                    <Cell gridColumn="1 / -1">
                        <Title variant="small">
                            <FormattedMessage defaultMessage="Geen accommodaties gevonden" />
                        </Title>
                    </Cell>
                )}
            </Grid>
            {moreAvailable && (
                <Flex justifyContent="center">
                    <Button submitting={isLoadingMore} variant="outline" onClick={loadMoreHandler}>
                        <FormattedMessage values={{ totalHits }} defaultMessage="Bekijk alle {totalHits} verblijven" />
                    </Button>
                </Flex>
            )}
        </Box>
    );
};

export default SearchRentalUnits;

const Grid = styled.div<{ moreAvailable: boolean }>`
    margin: 3.2rem 0;
    display: grid;
    row-gap: 4rem;
    column-gap: 3.2rem;
    position: relative;
    grid-template-columns: 1fr;
    z-index: 1;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        grid-template-columns: 1fr 1fr 1fr;
        row-gap: 6.4rem;
    }

    ::after {
        content: '';
        pointer-events: none;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 60rem;
        background: linear-gradient(-180deg, rgba(255, 255, 255, 0) 0%, rgb(255, 255, 255) 100%);
        transition: opacity 0.3s ease;
        opacity: ${({ moreAvailable }) => +moreAvailable};
        transform: translateZ(0);
    }
`;
